import React, { useState, useEffect } from 'react';
import { withNamespaces } from 'react-i18next';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import AppUrls from '../../config/AppUrls';
import { Routes as configRouter } from '../../config/Routes';
import { bindActionCreators, compose } from 'redux';
import ReferralService from '../../services/ReferralService';
import { showError, showSuccess } from '../../redux/actions/GlobalToastActions';
import * as GlobalModalActions from '../../redux/actions/GlobalModalActions';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { BoxWithTitle } from '../../components/common';
import LabelR from '../../components/Simple/LabelR';
import ModalTypes from '../../components/Modal/ModalTypes';
import NotFound from '../Generic/NotFound';
import MailboxIcon from '../../assets/img/rich_icons/mailbox.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { companyName } from '../../utils/Constants';

const GenericReferralSignUp = (props) => {
  const [referralIdParam, setReferralIdParam] = useState();
  const [externalSubMerchantId, setExternalSubMerchantId] = useState();

  const getInitialValues = () => {
    return {
      email: '',
      fullName: '',
      companyName: '',
    };
  };

  const handleSubmit = (values, actions) => {
    const {
      showError,
      showModal,
      hideModal,
      route: { history },
    } = props;

    const formData = {
      mainMerchantId: referralIdParam,
      externalSubMerchantId,
      email: values.userEmail,
      fullName: values.userFullName,
      companyName: values.userCompanyName,
      captchaResponse: values.recaptcha,
    };

    const promise = ReferralService.signUpGenericReferral(formData);

    promise.then(
      () => {
        actions.setSubmitting(false);
        showModal(
          {
            loadingOnConfirm: false,
            labelConfirm: 'labelSignUpSuccess',
            secondLabelConfirm: 'labelSignUpSuccessSubtitle',
            textBtnConfirm: 'buttonContinue',
            mainIcon: MailboxIcon,
            headerIcon: 'check-circle',
            type: 'info',
            title: 'labelSuccessfulRegistration',
            emailConfirmation: true,
            reverseButtons: false,
            disableCancel: true,
            onConfirm: () => {
              hideModal();
              history.push(configRouter.LOGIN);
            },
          },
          ModalTypes.CONFIRMATION_MODAL
        );
      },
      (error) => {
        const { response = {} } = error;
        const msgError = (response.data && response.data.description) || 'labelNetworkError';
        actions.setSubmitting(false);
        showError(msgError);
      }
    );
  };

  const getValidationSchema = () => {
    const { t } = this.props;
    return Yup.object().shape({
      userEmail: Yup.string().email(t('validationEmail')).required(t('validationRequired')),
      userFullName: Yup.string()
        .required(t('validationRequired'))
        .min(2, t('validationMinLength', { amount: 2 })),
      userCompanyName: Yup.string()
        .required(t('validationRequired'))
        .min(2, t('validationMinLength', { amount: 2 })),
      recaptcha: Yup.string().required(t('validationRequired')).nullable(),
    });
  };

  const handleCancel = () => {
    const {
      route: { history },
    } = this.props;
    history.push(configRouter.LOGIN);
  };

  useEffect(() => {
    setUrlParams();
  }, []);

  const setUrlParams = () => {
    const {
      route: {
        location: { search },
      },
    } = props;
    setReferralIdParam(new URLSearchParams(search).get('referralId') || new URLSearchParams(search).get('referralID'));
    setExternalSubMerchantId(new URLSearchParams(search).get('externalSubMerchantId') || new URLSearchParams(search).get('externalSubMerchantID'));
  };

  const { t } = props;

  return (
    <main className="root-container">
      <div className="mainContainer">
        {referralIdParam ? (
          <div className="signup-container">
            <div className="signup-header">
              <div className="col-lg-12">
                <div className="logo-container">
                  <img src={`${AppUrls.resources.logos}directa-logo-crop-color.png`} alt={companyName} className="logo" />
                </div>
              </div>
            </div>
            <div className="signup-title">
              <h3>{t('titleSignUp')}</h3>
            </div>
            <Formik
              initialValues={getInitialValues()}
              onSubmit={(values, actions) => {
                handleSubmit(values, actions);
              }}
              values
              enableReinitialize
              validationSchema={getValidationSchema}
            >
              {({ touched, values, setFieldValue, handleChange, errors, isSubmitting }) => (
                <Form>
                  <BoxWithTitle title={t('labelReferralSignUp')}>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="form-group form-item">
                          <LabelR htmlFor="email">{t('columnEmail')}</LabelR>
                          <Field
                            type="email"
                            name="userEmail"
                            className={`form-control ${errors.userEmail && touched.userEmail ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderEmail')}
                          />
                          <ErrorMessage name="userEmail" component="div" className="invalid-feedback" />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="form-group form-item">
                          <LabelR htmlFor="fullName">{t('labelFullName')}</LabelR>
                          <Field
                            type="fullName"
                            id="fullName"
                            name="userFullName"
                            className={`form-control ${errors.userFullName && touched.userFullName ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderFullName')}
                          />
                          <ErrorMessage name="userFullName" component="div" className="invalid-feedback" />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="form-group form-item">
                          <LabelR htmlFor="userCompanyName">{t('tabCompany')}</LabelR>
                          <Field
                            type="text"
                            name="userCompanyName"
                            className={`form-control ${errors.userCompanyName && touched.userCompanyName ? 'is-invalid' : ''}`}
                            placeholder={t('placeholderCompany')}
                          />
                          <ErrorMessage name="userCompanyName" component="div" className="invalid-feedback" />
                        </div>
                      </div>
                    </div>
                  </BoxWithTitle>
                  <div className="footer">
                    <div className="buttons-container">
                      <button type="button" className="btn btn-ghost" onClick={() => handleCancel}>
                        {t('buttonCancel')}
                      </button>
                      <button type="submit" className="btn btn-dark" disabled={isSubmitting}>
                        {isSubmitting && <FontAwesomeIcon icon="spinner" size="lg" spin className="mx-2" />}
                        {t('buttonSave')}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        ) : (
          <NotFound />
        )}
      </div>
    </main>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...GlobalModalActions,
      showError,
      showSuccess,
    },
    dispatch
  );

export default compose(withNamespaces(), connect(null, mapDispatchToProps))(GenericReferralSignUp);
